import React, { useEffect, useState } from "react";
import api, { formatIDR } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { TrendUp, TrendDown, Wallet, Receipt, Users, Package, ShoppingBag, Buildings, ShieldCheck, Warning, ChartLineUp, CaretRight } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { Link } from "react-router-dom";

const KpiCard = ({ label, value, icon: Icon, tone = "default", sub, testId }) => (
  <div className="bg-white border border-slate-200 rounded-md p-5 hover:-translate-y-0.5 hover:border-slate-300 transition-transform" data-testid={testId}>
    <div className="flex items-start justify-between">
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <Icon size={18} weight="duotone" className={tone === "positive" ? "text-emerald-600" : tone === "warning" ? "text-amber-600" : tone === "danger" ? "text-rose-600" : "text-slate-500"} />
    </div>
    <div className="mt-3 font-serif-display text-3xl font-semibold text-slate-900 tabular-nums">{value}</div>
    {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [taxGuard, setTaxGuard] = useState(null);
  const [cashflow, setCashflow] = useState(null);
  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setD(r.data));
    api.get("/ai/tax-guard/alerts").then((r) => setTaxGuard(r.data)).catch(() => setTaxGuard(null));
    api.get("/ai/briefing/weekly").then((r) => setCashflow(r.data)).catch(() => setCashflow(null));
  }, []);
  if (!d) return <div className="p-4 sm:p-6 lg:p-8"><Skeleton className="h-96" /></div>;
  const k = d.kpis || {};

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl" data-testid="dashboard-page">
      <div>
        <h1 className="font-serif-display text-4xl font-medium text-slate-900">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Ringkasan pembukuan bisnis Anda.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <KpiCard label="Pendapatan" value={formatIDR(k.revenue)} icon={TrendUp} tone="positive" testId="kpi-revenue" />
        <KpiCard label="Beban" value={formatIDR(k.expenses)} icon={TrendDown} tone="danger" testId="kpi-expenses" />
        <KpiCard label="Laba Bersih" value={formatIDR(k.net_income)} icon={Wallet} tone="positive" testId="kpi-net" />
        <KpiCard label="Aktiva Tetap" value={k.fixed_assets || 0} icon={Buildings} testId="kpi-fa" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        <KpiCard label="Total Penjualan" value={formatIDR(k.total_sales)} icon={ShoppingBag} sub={`Piutang: ${formatIDR(k.ar_outstanding)}`} tone="warning" testId="kpi-sales" />
        <KpiCard label="Total Pembelian" value={formatIDR(k.total_purchase)} icon={ShoppingBag} sub={`Hutang: ${formatIDR(k.ap_outstanding)}`} testId="kpi-purchase" />
        <KpiCard label="Pelanggan / Vendor" value={`${k.customers || 0} / ${k.vendors || 0}`} icon={Users} testId="kpi-partners" />
        <KpiCard label="Produk / Jasa" value={`${k.products || 0} / ${k.services || 0}`} icon={Package} sub={`${k.low_stock || 0} stok kritis`} tone={k.low_stock > 0 ? "warning" : "default"} testId="kpi-products" />
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-md p-6" data-testid="chart-trend">
        <h3 className="font-serif-display text-2xl font-medium">Pendapatan vs Beban</h3>
        <div className="text-xs text-slate-500">6 bulan terakhir</div>
        <div className="h-64 mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={d.revenue_trend} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" stroke="#94A3B8" fontSize={11} />
              <YAxis stroke="#94A3B8" fontSize={11} tickFormatter={(v) => (v / 1000000).toFixed(0) + "jt"} />
              <Tooltip formatter={(v) => formatIDR(v)} contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #E2E8F0" }} />
              <Bar dataKey="revenue" fill="#1E293B" name="Pendapatan" radius={[4, 4, 0, 0]} />
              <Bar dataKey="expenses" fill="#D97706" name="Beban" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
        <TaxGuardWidget data={taxGuard} />
        <CashflowWidget data={cashflow} />
      </div>
    </div>
  );
}

const SevDot = ({ sev }) => (
  <span className={`inline-block w-2 h-2 rounded-full mt-1.5 shrink-0 ${sev === "error" ? "bg-rose-500" : sev === "warning" ? "bg-amber-500" : "bg-sky-500"}`} />
);

function TaxGuardWidget({ data }) {
  if (!data) return null;
  const alerts = data.alerts || [];
  const s = data.summary || {};
  return (
    <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="widget-tax-guard">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} weight="duotone" className="text-emerald-600" />
          <h3 className="font-serif-display text-xl font-medium">AI Tax Guard</h3>
        </div>
        <span className="text-[11px] text-slate-400">Masa {data.period?.month_start?.slice(0, 7)}</span>
      </div>
      {alerts.length === 0 ? (
        <div className="mt-4 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded p-3">
          ✅ Semua kewajiban pajak terpantau aman. PPN Keluaran {formatIDR(s.ppn_keluaran)} · Masukan {formatIDR(s.ppn_masukan)} · NPWP/NITKU partner PKP valid.
        </div>
      ) : (
        <div className="mt-4 space-y-2.5">
          {alerts.map((a) => (
            <div key={a.code} className="flex items-start gap-2.5 border border-slate-100 rounded p-3 hover:border-slate-200 transition-colors" data-testid={`tax-alert-${a.code}`}>
              <SevDot sev={a.severity} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900">{a.title}</div>
                <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">{a.detail}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CashflowWidget({ data }) {
  if (!data) return null;
  const p = data.prediction || {};
  const m = data.metrics || {};
  const anomalies = data.anomalies || [];
  const critical = p.cash_runway_critical;
  return (
    <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="widget-cashflow">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ChartLineUp size={18} weight="duotone" className="text-sky-600" />
          <h3 className="font-serif-display text-xl font-medium">Prediksi Arus Kas 30 Hari</h3>
        </div>
        <span className="text-[11px] text-slate-400">s/d {p.horizon}</span>
      </div>
      <div className="mt-4">
        <div className="text-xs uppercase tracking-wider text-slate-500">Proyeksi saldo kas</div>
        <div className={`mt-1 font-serif-display text-3xl font-semibold tabular-nums ${critical ? "text-rose-600" : "text-slate-900"}`} data-testid="cashflow-projected">
          {formatIDR(p.projected_cash_end)} {critical && <Warning size={20} weight="fill" className="inline text-rose-500 -mt-1" />}
        </div>
        {critical && <div className="text-xs text-rose-600 mt-1">⚠️ Proyeksi negatif — percepat penagihan piutang atau tunda pembayaran hutang.</div>}
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mt-4 text-xs">
        <div className="flex justify-between"><span className="text-slate-500">Kas saat ini</span><span className="tabular-nums font-medium">{formatIDR(p.starting_cash)}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">Tagihan masuk (est.)</span><span className="tabular-nums font-medium text-emerald-600">+{formatIDR(p.expected_ar_collection)}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">Hutang jatuh tempo</span><span className="tabular-nums font-medium text-rose-600">−{formatIDR(p.expected_ap_payment)}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">Proyeksi beban</span><span className="tabular-nums font-medium text-rose-600">−{formatIDR(p.projected_expense)}</span></div>
        <div className="flex justify-between col-span-2 pt-1.5 border-t border-slate-100">
          <span className="text-slate-500">Penjualan minggu ini</span>
          <span className="tabular-nums font-medium">{formatIDR(m.sales_this_week)} <span className="text-slate-400">({m.sales_growth >= 0 ? "+" : ""}{(m.sales_growth * 100).toFixed(1)}%)</span></span>
        </div>
      </div>
      {anomalies.length > 0 && (
        <div className="mt-4 space-y-1.5">
          {anomalies.slice(0, 3).map((a, i) => (
            <div key={i} className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-100 rounded px-2.5 py-1.5" data-testid={`anomaly-${i}`}>
              <Warning size={12} weight="fill" className="text-amber-500 mt-0.5 shrink-0" />
              <div><b>{a.title}:</b> <span className="text-slate-600">{a.detail}</span></div>
            </div>
          ))}
        </div>
      )}
      <Link to="/app/settings" className="mt-4 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 transition-colors">
        Jalankan AI Briefing lengkap di Pengaturan → Job Desk AI <CaretRight size={12} />
      </Link>
    </div>
  );
}
